import {
  useForm,
  FormProvider,
  type UseFormGetValues,
  type UseFormReset,
  type Path,
  type PathValue,
  type FieldErrors,
} from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Users, Rocket } from 'lucide-react';
import { useRef, useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Card, CardHeader, CardContent } from '@lean-agility/design-system/react';
import { TeamTab, Collapsible, SectionHeader, AddButton, ConflictDialog } from '@/components/molecules';
import { SimulationActionBar } from '@/components/organisms';
import { TeamForm } from './TeamForm';
import { FeatureForm } from './FeatureForm';
import { simulationFormSchema, type SimulationFormData } from '@/lib/validation';
import { formValuesToRequest, getTeamColor, TEAM_COLOR_PALETTE } from '@/lib/utils';
import { runOrganizationSimulation, createTeam, deleteTeam, postTeamMove, ApiError } from '@/lib/api';
import { anchorAbove } from '@/lib/ordering';
import { useErrorHandler, useSessionState, useConflictHandler } from '@/hooks';
import {
  createFieldArrayRegistry,
  FieldArrayRegistryContext,
  useSyncedFieldArray,
  type FieldArrayRegistry,
} from './syncedFieldArray';
import { ConflictContext, useWriteErrors } from '@/hooks/useWriteErrors';
import { useAuth } from '@/features/auth';
import { FeatureImportCard } from '@/features/feature-import';
import { useLicenseStatus, licenseQueryKeys } from '@/features/license';
import type { OrganizationMonteCarloResponse, SimulationFormValues } from '@/types/api';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
// Separate component for Features section
function FeaturesCard({ simulationId }: { simulationId?: string | null }) {
  // Feature count is reported up from FeatureForm so the summary updates reactively.
  const [featureCount, setFeatureCount] = useState(0);

  // Persist collapse state in session storage (survives navigation, clears on tab close)
  const [isExpanded, setIsExpanded] = useSessionState(`features-expanded-${simulationId ?? 'new'}`, true);

  return (
    <Card>
      <CardHeader>
        <SectionHeader
          icon={Rocket}
          title="Features"
          subtitle="Group deliverables by feature for better organization"
          // Tracker feature-import cluster: gear (settings dialog), Import, run status
          action={<FeatureImportCard simulationId={simulationId ?? null} />}
        />
      </CardHeader>
      <Collapsible
        header={<span className="text-sm font-medium text-text-secondary">Feature List</span>}
        summary={`${featureCount} features`}
        isExpanded={isExpanded}
        onExpandedChange={setIsExpanded}
        className="px-6 pb-2"
        contentClassName="pt-0"
      >
        <CardContent variant="flush">
          <FeatureForm simulationId={simulationId} onFeatureCountChange={setFeatureCount} />
        </CardContent>
      </Collapsible>
    </Card>
  );
}

// Handle interface for exposing form methods via ref
export interface SimulationFormHandle {
  setValue: <TFieldName extends Path<SimulationFormValues>>(
    name: TFieldName,
    value: PathValue<SimulationFormValues, TFieldName>,
    options?: { shouldDirty?: boolean; shouldValidate?: boolean },
  ) => void;
  getValues: UseFormGetValues<SimulationFormValues>;
  reset: UseFormReset<SimulationFormValues>;
  /** The lists on screen, for changing them without remounting their rows. */
  fieldArrays: FieldArrayRegistry;
}

interface SimulationFormProps {
  onResult: (result: OrganizationMonteCarloResponse) => void;
  defaultValues: SimulationFormValues;
  onFormDataChange: (data: SimulationFormValues) => void;
  // Optional: if provided, use persistent simulation endpoints
  simulationId?: string | null;
  onRunPersistent?: (formValues: SimulationFormValues, numSimulations: number) => Promise<void>;
  // Callback to refresh form data after import (fetches from server and resets form)
  onRefresh?: () => Promise<void>;
  // Controlled active team tab (persisted via URL)
  activeTeamIndex?: number;
  onActiveTeamIndexChange?: (index: number) => void;
  // Whether a background simulation run is in progress (from LayoutContext)
  isSimulationRunning?: boolean;
  // Deliverable UUID to focus on mount (e.g., navigated from timeline view)
  focusDeliverableId?: string;
}

export const SimulationForm = forwardRef<SimulationFormHandle, SimulationFormProps>(function SimulationForm(
  {
    onResult,
    defaultValues,
    onFormDataChange,
    simulationId,
    onRunPersistent,
    onRefresh,
    activeTeamIndex: controlledActiveTeamIndex,
    onActiveTeamIndexChange,
    isSimulationRunning,
    focusDeliverableId,
  },
  ref,
) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAddingTeam, setIsAddingTeam] = useState(false);
  // The open team's position, as the URL records it (controlled when provided).
  const [localActiveTeamIndex, setLocalActiveTeamIndex] = useState(0);
  const urlTeamIndex = controlledActiveTeamIndex ?? localActiveTeamIndex;
  const setActiveTeamIndex = onActiveTeamIndexChange ?? setLocalActiveTeamIndex;
  const [highlightedDeliverableId, setHighlightedDeliverableId] = useState<string | null>(null);

  // Error handling via toasts
  const { handleError } = useErrorHandler('Simulation');
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Proactive license gating — pre-check before user clicks Run
  const { data: licenseStatus } = useLicenseStatus();
  const hasValidLicense = licenseStatus?.status === 'valid' || licenseStatus?.status === 'expiring_soon';

  // Track when we're waiting for a new team via SSE
  const pendingNewTeamRef = useRef(false);
  const prevTeamCountRef = useRef<number | null>(null);

  const methods = useForm<SimulationFormValues, unknown, SimulationFormData>({
    resolver: zodResolver(simulationFormSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const { control, handleSubmit, getValues, reset, setValue } = methods;

  // The lists on screen lend their operations to the cache sync (shipcast-nad4).
  const [fieldArrays] = useState(createFieldArrayRegistry);

  // Expose form methods for the cache sync (ConfigPage's useFormCacheSync)
  useImperativeHandle(
    ref,
    () => ({
      setValue,
      getValues,
      reset,
      fieldArrays,
    }),
    [setValue, getValues, reset, fieldArrays],
  );

  const {
    fields: teamFields,
    append: appendTeam,
    remove: removeTeam,
    move: moveTeam,
    drag: teamDrag,
  } = useSyncedFieldArray({ control, name: 'teams' }, fieldArrays);

  // The open team is a team, not a position (shipcast-nad4): when the tabs are
  // reordered — here or by another user — the same team stays open. It is held
  // as its tab's row key, which travels with the team, and decided during
  // render, so no frame ever shows another team in its place. The URL keeps
  // the position: a change of it (a click, back/forward) opens the team there,
  // and a reorder moves the URL to the team's new position. A key no tab has
  // any more (a form reset gives every row a new one) re-anchors to the URL.
  const [openTeamKey, setOpenTeamKey] = useState(() => teamFields[urlTeamIndex]?.id ?? null);
  const [seenUrlTeamIndex, setSeenUrlTeamIndex] = useState(urlTeamIndex);
  const openTeamKeyIndex = openTeamKey ? teamFields.findIndex((f) => f.id === openTeamKey) : -1;
  const urlTeamKey = teamFields[urlTeamIndex]?.id ?? null;
  if (urlTeamIndex !== seenUrlTeamIndex || (openTeamKeyIndex < 0 && urlTeamKey !== openTeamKey)) {
    setSeenUrlTeamIndex(urlTeamIndex);
    setOpenTeamKey(urlTeamKey);
  }
  const activeTeamIndex = openTeamKeyIndex >= 0 ? openTeamKeyIndex : urlTeamIndex;
  useEffect(() => {
    if (openTeamKeyIndex >= 0 && openTeamKeyIndex !== urlTeamIndex) setActiveTeamIndex(openTeamKeyIndex);
  }, [openTeamKeyIndex, urlTeamIndex, setActiveTeamIndex]);

  // A refused write the screen cannot fix by retrying opens the conflict dialog.
  const { conflictState, reportConflict, clearConflict } = useConflictHandler();
  const [isRefreshingAfterConflict, setIsRefreshingAfterConflict] = useState(false);
  const { onMoveError: onTeamMoveError } = useWriteErrors('team', handleError);
  const refreshAfterConflict = async () => {
    setIsRefreshingAfterConflict(true);
    try {
      await onRefresh?.();
      clearConflict();
    } finally {
      setIsRefreshingAfterConflict(false);
    }
  };

  // Switch to newly added team when it arrives via SSE (uses teamFields.length from useFieldArray)
  useEffect(() => {
    const currentCount = teamFields.length;
    // Initialize on first render
    if (prevTeamCountRef.current === null) {
      prevTeamCountRef.current = currentCount;
      return;
    }
    if (pendingNewTeamRef.current && currentCount > prevTeamCountRef.current) {
      // A new team was added - switch to it
      setActiveTeamIndex(currentCount - 1);
      pendingNewTeamRef.current = false;
    }
    prevTeamCountRef.current = currentCount;
  }, [teamFields.length, setActiveTeamIndex]);

  // DnD sensors for team tabs
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Handle team tab drag end. The open team follows its tab on its own.
  const handleTeamDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    const oldIndex = teamFields.findIndex((f) => f.id === active.id);
    const newIndex = over ? teamFields.findIndex((f) => f.id === over.id) : -1;
    const moved = oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex;
    if (!moved) {
      teamDrag.ended(false);
      return;
    }

    // Capture team IDs BEFORE moving (form data will change after moveTeam)
    const teamIds = teamFields.map((_, idx) => getValues(`teams.${idx}.id`));
    const movedIds = arrayMove(teamIds, oldIndex, newIndex);
    const movedTeamId = movedIds[newIndex];
    moveTeam(oldIndex, newIndex);
    teamDrag.ended(true);

    // Send the move itself, not this screen's whole list: the server applies
    // it to the current order, so another user's concurrent move survives.
    if (simulationId && movedTeamId) {
      try {
        await postTeamMove(simulationId, movedTeamId, anchorAbove(movedIds, newIndex));
      } catch (err) {
        onTeamMoveError(err);
      }
    }
  };

  const addTeam = async () => {
    const teamName = `Team ${teamFields.length + 1}`;
    const teamData = {
      name: teamName,
      throughput_data: '5, 5, 5',
      throughput_scale: 1.0,
      deliverables: [
        {
          feature_id: null,
          name: '',
          unfinished_work_items: 10,
          unknown_min: null, // null = not decided (defaults to 0 in simulation)
          unknown_max: null, // null = not decided (defaults to min in simulation)
          predecessors: [],
          due_date: '',
          available_to_start_min: '',
          available_to_start_max: '',
        },
      ],
      wip: 2,
    };

    // Auto-create team in database if we have a simulationId
    if (simulationId) {
      setIsAddingTeam(true);
      pendingNewTeamRef.current = true; // Track that we're waiting for SSE
      try {
        // Assign a color from the palette based on the next team index
        const nextTeamIndex = teamFields.length;
        const teamColor = TEAM_COLOR_PALETTE[nextTeamIndex % TEAM_COLOR_PALETTE.length];

        await createTeam(simulationId, {
          name: teamName,
          wip: 2,
          throughput_data: [5, 5, 5],
          throughput_scale: 1.0,
          color: teamColor,
        });
        // Don't appendTeam here - let SSE event handle adding to formData
        // This prevents duplicate teams (one from appendTeam, one from SSE)
        // The tab index will be set when formData updates via useEffect
      } catch (err) {
        pendingNewTeamRef.current = false;
        handleError(err);
        // On error, still add to form so user can retry
        appendTeam(teamData);
        setActiveTeamIndex(teamFields.length);
      } finally {
        setIsAddingTeam(false);
      }
    } else {
      appendTeam(teamData);
      setActiveTeamIndex(teamFields.length);
    }
  };

  const handleRemoveTeam = async (index: number) => {
    const team = getValues(`teams.${index}`);

    // Auto-delete team from database if saved
    if (simulationId && team.id) {
      try {
        await deleteTeam(team.id);
      } catch (err) {
        handleError(err);
        return; // Don't remove from form if delete failed
      }
    }

    removeTeam(index);
    // Adjust active tab if necessary
    if (activeTeamIndex >= teamFields.length - 1) {
      setActiveTeamIndex(Math.max(0, teamFields.length - 2));
    }
  };

  // Reset active tab when teams change dramatically (e.g., import)
  useEffect(() => {
    if (activeTeamIndex >= teamFields.length) {
      setActiveTeamIndex(Math.max(0, teamFields.length - 1));
    }
  }, [teamFields.length, activeTeamIndex, setActiveTeamIndex]);

  // Clear highlight after a delay
  useEffect(() => {
    if (highlightedDeliverableId) {
      const timer = setTimeout(() => {
        setHighlightedDeliverableId(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [highlightedDeliverableId]);

  // Navigate to a dependency: switch to the correct team tab and highlight the deliverable.
  // Scrolling is handled by TeamForm's useEffect which uses virtualizer.scrollToIndex
  // (or scrollIntoView for non-virtualized lists).
  const handleNavigateToDependency = useCallback(
    (deliverableId: string, teamName: string) => {
      const teams = getValues('teams');
      const teamIndex = teams.findIndex((t) => t.name === teamName);

      if (teamIndex === -1) {
        console.warn(`Team "${teamName}" not found`);
        return;
      }

      setActiveTeamIndex(teamIndex);
      setHighlightedDeliverableId(deliverableId);
    },
    [getValues, setActiveTeamIndex],
  );

  // Focus a deliverable on mount (e.g., navigated from timeline view).
  // ConfigPage clears the focus param from the URL after consumption,
  // so this effect runs once per navigation intent.
  // Logic is inlined (rather than calling handleNavigateToDependency) to avoid
  // depending on a callback whose identity changes after the team tab switch.
  useEffect(() => {
    if (!focusDeliverableId) return;

    const teams = getValues('teams');
    for (const team of teams) {
      const found = team.deliverables.some((d) => d.id === focusDeliverableId);
      if (found) {
        const teamIndex = teams.findIndex((t) => t.name === team.name);
        if (teamIndex !== -1) {
          setActiveTeamIndex(teamIndex);
          setHighlightedDeliverableId(focusDeliverableId);
        }
        return;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot effect on focusDeliverableId arrival
  }, [focusDeliverableId]);

  const onSubmit = async (data: SimulationFormData) => {
    setIsSubmitting(true);

    // Save form data before making API call.
    // `data` is the schema OUTPUT (post-validation); it is structurally a valid
    // form-value input, so it flows into these input-typed callbacks without a cast.
    onFormDataChange(data);

    try {
      // If we have a simulationId and onRunPersistent callback, use persistent endpoints
      if (simulationId && onRunPersistent) {
        await onRunPersistent(data, data.num_simulations);
      } else {
        // Otherwise, use one-off simulation endpoint
        const request = formValuesToRequest(data);
        const result = await runOrganizationSimulation(request);
        onResult(result);
      }
    } catch (err) {
      if (err instanceof ApiError && err.code === 'LICENSE_REQUIRED') {
        // Invalidate cached license status so proactive UI updates
        void queryClient.invalidateQueries({ queryKey: licenseQueryKeys.status() });
      } else {
        handleError(err);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle validation errors
  const onValidationError = (validationErrors: FieldErrors<SimulationFormValues>) => {
    console.error('Form validation errors:', validationErrors);
    // Extract first error message from nested errors
    const getFirstError = (obj: unknown): string | undefined => {
      if (!obj || typeof obj !== 'object') return undefined;
      if ('message' in obj && typeof (obj as { message?: string }).message === 'string') {
        return (obj as { message: string }).message;
      }
      for (const value of Object.values(obj)) {
        const result = getFirstError(value);
        if (result) return result;
      }
      return undefined;
    };
    const firstError = getFirstError(validationErrors);
    if (firstError) {
      handleError(`Validation error: ${firstError}`);
    }
  };

  // Save form data when user navigates away (blur on the form)
  const handleFormBlur = () => {
    const currentValues = getValues();
    onFormDataChange(currentValues);
  };

  return (
    <FormProvider {...methods}>
      <FieldArrayRegistryContext.Provider value={fieldArrays}>
        <ConflictContext.Provider value={reportConflict}>
          <form onSubmit={handleSubmit(onSubmit, onValidationError)} onBlur={handleFormBlur} className="space-y-6">
            {/* Action Bar - Only shown for saved simulations */}
            {simulationId && (
              <SimulationActionBar
                simulationId={simulationId}
                isSubmitting={isSubmitting}
                isSimulationRunning={isSimulationRunning}
                onRefresh={onRefresh}
                licenseBlocked={!hasValidLicense}
                licenseMessage={
                  !hasValidLicense ? (
                    user?.is_admin ? (
                      <>
                        A valid license is required.{' '}
                        <Link
                          to="/admin/license"
                          className="
                            font-medium text-action-primary
                            hover:underline
                          "
                        >
                          Manage license
                        </Link>
                      </>
                    ) : (
                      'A valid license is required. Contact your administrator.'
                    )
                  ) : undefined
                }
              />
            )}

            {/* Features Section */}
            <FeaturesCard simulationId={simulationId} />

            {/* Teams */}
            <Card>
              <CardHeader>
                <SectionHeader
                  icon={Users}
                  title="Teams"
                  subtitle="Define teams and their simulation data"
                  action={<AddButton label="Team" onClick={addTeam} isLoading={isAddingTeam} />}
                />
              </CardHeader>
              <CardContent variant="flush" className="space-y-0">
                {/* Team Tabs with Drag & Drop */}
                <div
                  role="tablist"
                  aria-label="Teams"
                  className="flex flex-wrap items-center gap-1 border-b border-border-default/50 px-4 pt-3"
                >
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    onDragStart={teamDrag.started}
                    onDragCancel={() => teamDrag.ended(false)}
                    onDragEnd={handleTeamDragEnd}
                  >
                    <SortableContext items={teamFields.map((f) => f.id)} strategy={horizontalListSortingStrategy}>
                      {teamFields.map((field, index) => {
                        // Use watch() instead of getValues() to subscribe to changes.
                        // react-hook-form's watch() returns a fresh value each render by design and
                        // is not memoizable, so the React Compiler readiness lint can't verify it (ref shipcast-7i0).
                        // eslint-disable-next-line react-hooks/incompatible-library
                        const teamName = methods.watch(`teams.${index}.name`) || `Team ${index + 1}`;
                        const storedColor = methods.watch(`teams.${index}.color`);
                        const teamColor = getTeamColor(index, storedColor);
                        const isActive = activeTeamIndex === index;

                        return (
                          <TeamTab
                            key={field.id}
                            id={field.id}
                            name={teamName}
                            color={teamColor}
                            isActive={isActive}
                            onClick={() => setActiveTeamIndex(index)}
                          />
                        );
                      })}
                    </SortableContext>
                  </DndContext>
                </div>

                {/* Active Team Form - Only render the active team to avoid performance issues */}
                {teamFields[activeTeamIndex] && (
                  <TeamForm
                    key={teamFields[activeTeamIndex].id}
                    teamIndex={activeTeamIndex}
                    onRemove={() => handleRemoveTeam(activeTeamIndex)}
                    canRemove={teamFields.length > 1}
                    simulationId={simulationId}
                    onRefresh={onRefresh}
                    onDependencyClick={handleNavigateToDependency}
                    highlightedDeliverableId={highlightedDeliverableId}
                    isActive={true}
                  />
                )}
              </CardContent>
            </Card>
          </form>
          <ConflictDialog
            isOpen={conflictState.hasConflict}
            onClose={clearConflict}
            onRefresh={refreshAfterConflict}
            resourceType={conflictState.resourceType}
            isRefreshing={isRefreshingAfterConflict}
          />
        </ConflictContext.Provider>
      </FieldArrayRegistryContext.Provider>
    </FormProvider>
  );
});
